import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { settingsAPI, oltAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  Router, Settings, Wifi, AlertTriangle, Plus, Trash2,
  Eye, EyeOff, CheckCircle, XCircle, Loader, Save, RefreshCw, Receipt,
  Server, Pencil, Search, MessagesSquare, CreditCard, QrCode, Building2,
  Image as ImageIcon, Upload, X, FileClock, Smartphone, Cable, Tv, Shuffle, Activity, LayoutDashboard, FileSpreadsheet,
  ChevronRight, ArrowLeft
} from 'lucide-react';
import { SmsTab } from './MessagingApiPage';
import GoogleSheetsSettingsTab from './GoogleSheetsSettingsTab';
import AdsPage from './AdsPage';
import ImportPage from './ImportPage';
import HistoryImportPage from './HistoryImportPage';
import AppViewSettingsTab from './AppViewSettingsTab';
import DashboardWidgetSettingsTab from './DashboardWidgetSettingsTab';
import GatewayRoutingTab from '../components/settings/GatewayRoutingTab';
import EntityGatewaysTab from '../components/settings/EntityGatewaysTab';
import AmountSplitGateways from '../components/settings/AmountSplitGateways';
import useDarkReady from '../theme/useDarkReady';
import { useViewMode } from '../hooks/useViewMode';

// App view menu text: [what it does, search keywords]. Keyed like tabGroups' items.
const APP_INFO = {
  billing: ['GST on/off, default GST %, receipt numbers, WhatsApp template note', 'gst tax receipt number numbering invoice'],
  company: ['Name, address, GSTIN, UPI and logo on receipts (CableTV + Broadband)', 'logo terms conditions gstin upi address phone email receipt'],
  connection: ['OLT IP, login and device name. Has Test + Save', 'olt ip port username password ssl make model firmware'],
  ports: ['Name the OLT PON ports. Saved with the OLT Save button', 'pon slot port label olt'],
  'ge-ports': ['Name the uplink ports used in alert messages', 'ge uplink isp olt alert'],
  power: ['Good / weak signal limits for ONU power colours', 'rx tx dbm optical signal threshold olt'],
  nas: ['MikroTik routers: add, edit, test, WAN interfaces', 'nas mikrotik router wan routeros'],
  sms: ['SMS gateway and messaging API', 'sms messaging api gateway'],
  payments: ['Online gateway and UPI QR for CableTV and Broadband', 'payment gateway razorpay ablepay upi qr surcharge fee'],
  imbs: ['Netzur token, site id and panel login', 'netzur imbs token site panel ekyc'],
  playboxtv: ['PlayBoxTV API keys', 'playbox playboxtv api key partner'],
  googleSheets: ['Google Sheets sync', 'google sheet sync service account'],
  ads: ['Advertisement banner in the customer app', 'ads advertisement banner'],
  import: ['Import customers and data from a file', 'import csv excel upload customers'],
  'history-import': ['Import old CableTV payments', 'history payment import catv'],
  'history-import-broadband': ['Import old Broadband payments', 'history payment import broadband'],
  'app-view': ['Choose bottom tabs and menu items', 'bottom tab menu app view'],
  'dashboard-widgets': ['Show or hide dashboard widgets', 'dashboard widgets'],
};
const OLT_TABS = ['connection', 'ports', 'ge-ports', 'power'];

// ─── helpers ──────────────────────────────────────────────────────────────────
const MASKED = '••••••••';

const SECTION = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{
      fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 14,
      paddingBottom: 6, borderBottom: '1px solid var(--border)'
    }}>{title}</div>
    {children}
  </div>
);

const FIELD = ({ label, hint, required, children }) => (
  <div className="form-group">
    <label className="form-label">{label}{required && <span className="required"> *</span>}</label>
    {children}
    {hint && <div className="form-hint">{hint}</div>}
  </div>
);

// ─── Power threshold visual bar ───────────────────────────────────────────────
function PowerBar({ min, max, goodMin, goodMax }) {
  const range = max - min || 1;
  const toP = (v) => Math.max(0, Math.min(100, ((v - min) / range) * 100));
  const goodL = toP(goodMin);
  const goodW = toP(goodMax) - goodL;

  return (
    <div style={{ margin: '8px 0 4px' }}>
      <div style={{ position: 'relative', height: 18, borderRadius: 9, background: 'var(--cb-fee2e2)', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: `${goodL}%`, width: `${goodW}%`, height: '100%', background: 'var(--cb-d1fae5)' }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px', fontSize: '.7rem', fontWeight: 600, color: 'var(--c-6b7280)' }}>
          <span>{min} dBm</span>
          <span style={{ color: 'var(--c-065f46)' }}>Good: {goodMin} to {goodMax}</span>
          <span>{max} dBm</span>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.7rem', color: 'var(--text-muted)', marginTop: 3 }}>
        <span style={{ color: 'var(--c-991b1b)' }}>Weak / Critical</span>
        <span style={{ color: 'var(--c-065f46)' }}>Acceptable range</span>
        <span style={{ color: 'var(--c-92400e)' }}>Saturated</span>
      </div>
    </div>
  );
}

// ─── Main Settings Page ───────────────────────────────────────────────────────
export default function SettingsPage() {
  useDarkReady();
  // Reads ?tab= once on mount so a link (e.g. the GTPL nav dropdown's "Session & Settings"
  // entry) can land directly on a specific tab instead of always opening on Billing. Doesn't
  // stay synced to the URL afterward — clicking between tabs in-page is plain local state,
  // same as before.
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'billing');
  // App view: a menu of categories first, then one setting screen with a Back bar.
  const { viewMode } = useViewMode();
  const isApp = viewMode === 'app' || window.innerWidth < 640;
  const [appOpen, setAppOpen] = useState(() => !!new URLSearchParams(window.location.search).get('tab'));
  const [appSearch, setAppSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // null | 'success' | 'failed'
  const [showPass, setShowPass] = useState(false);

  const [billing, setBilling] = useState({ gstEnabled: true, defaultGstPercent: 18, whatsappTemplates: [] });
  const [billingSaving, setBillingSaving] = useState(false);
  // `billing.whatsappTemplates` is still saved by this page (handleSaveBilling posts the whole
  // billing object), but is now EDITED in Message Center → Customer Notify, next to the
  // auto-reminder toggles that actually consume it.

  // ── Receipt Numbering state (CATV: LCV<n> forever; Broadband: <FY>/<n>, resets each FY) ──
  const [receiptNum, setReceiptNum] = useState({ catv: { nextNumber: 1 }, broadband: { fyLabel: '', nextNumber: 1 } });
  const [receiptNumForm, setReceiptNumForm] = useState({ catvNext: '', broadbandNext: '' });
  const [receiptNumSaving, setReceiptNumSaving] = useState(false);

  // ── Company Details state (separate identity for CATV vs Broadband invoices) ──
  const emptyCompanyDetail = { name: '', address: '', phone: '', email: '', gstin: '', upiId: '', upiName: '', logo: '', termsAndConditions: [] };
  const [company, setCompany] = useState({ catv: { ...emptyCompanyDetail }, broadband: { ...emptyCompanyDetail } });
  const [companySaving, setCompanySaving] = useState(false);
  const setCompanyField = (type, key, val) => setCompany(c => ({ ...c, [type]: { ...c[type], [key]: val } }));

  // Logos only ever render at ~50-160px in receipts/PDFs — resizing to this on upload keeps
  // the resulting data URI small (typically a few tens of KB) regardless of the source photo's
  // size, so it can't trip a reverse proxy's request body-size limit (a real phone photo can be
  // several MB, and that limit lives outside this app's own config — see PUT /settings/company).
  const MAX_LOGO_SOURCE_BYTES = 15 * 1024 * 1024; // 15MB — just a sanity cap before decoding
  const MAX_LOGO_DIMENSION = 300;
  const handleLogoUpload = (type, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Logo must be an image file'); return; }
    if (file.size > MAX_LOGO_SOURCE_BYTES) { toast.error('Image file is too large'); return; }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_LOGO_DIMENSION / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale) || 1;
      const h = Math.round(img.height * scale) || 1;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      setCompanyField(type, 'logo', canvas.toDataURL('image/png'));
      URL.revokeObjectURL(objectUrl);
    };
    img.onerror = () => { toast.error('Failed to read image file'); URL.revokeObjectURL(objectUrl); };
    img.src = objectUrl;
  };

  const [form, setForm] = useState({
    // Connection
    ip: '', port: 80, username: 'admin', password: MASKED, useSSL: false,
    // Identity
    make: 'SYROTECH', model: '', deviceName: '', location: '', firmwareVersion: '',
    // PON ports
    ponPorts: [],
    // GE (uplink) ports — display names only
    gePorts: [],
    // Power
    power: { minRxPower: -30, maxRxPower: -8, goodRxMin: -27, goodRxMax: -12, minTxPower: -5, maxTxPower: 5 },
    isEnabled: true,
  });

  const set = useCallback((key, val) => setForm(f => ({ ...f, [key]: val })), []);
  const setPow = useCallback((key, val) => setForm(f => ({ ...f, power: { ...f.power, [key]: parseFloat(val) || 0 } })), []);

  // ── NAS state ──
  const NAS_MASKED = '••••••••';
  const emptyNAS = { name: '', ip: '', port: 8628, restPort: 80, username: '', password: '', useSSL: false, apiMode: 'rest', isEnabled: true, wanInterfaces: [] };
  const [nasList, setNasList] = useState([]);
  const [nasForm, setNasForm] = useState(emptyNAS);
  const [editingNasId, setEditingNasId] = useState(null); // null = adding new, string = editing
  const [showNasForm, setShowNasForm] = useState(false);
  const [nasShowPass, setNasShowPass] = useState(false);
  const [nasSaving, setNasSaving] = useState(false);
  const [nasDeleting, setNasDeleting] = useState(null);
  const [nasTesting, setNasTesting] = useState(null);
  const [nasDiscoveredIfaces, setNasDiscoveredIfaces] = useState([]);
  const [nasDiscovering, setNasDiscovering] = useState(false);
  const setNas = (key, val) => setNasForm(f => ({ ...f, [key]: val }));

  // ── Payment Gateway state ── CableTV and Broadband are fully independent gateways
  // (2026-07-27) — each keyed by service in one state object rather than duplicating
  // six state variables per service.
  const emptyFeesByMethod = {
    upi: { percent: 0, flat: 0 }, debitCard: { percent: 0, flat: 0 },
    creditCard: { percent: 0, flat: 0 }, netbanking: { percent: 0, flat: 0 },
  };
  const emptyPaymentGateway = {
    provider: 'razorpay', keyId: '', keySecret: '', webhookSecret: '',
    testMode: true, isEnabled: false,
    ablepayApiKey: '', ablepaySalt: '', ablepayPgApiUrl: '',
    ablepayShowConvenienceFee: true,
    passFeeToCustomer: false, feesByMethod: emptyFeesByMethod,
  };
  const emptyPgUi = { saving: false, testing: false, testResult: null, showSecret: false, showWebhookSecret: false, showAblepaySalt: false };
  const [paymentGateways, setPaymentGateways] = useState({ catv: emptyPaymentGateway, broadband: emptyPaymentGateway });
  const [pgUi, setPgUi] = useState({ catv: emptyPgUi, broadband: emptyPgUi });
  const setPg = (service, key, val) => setPaymentGateways(f => ({ ...f, [service]: { ...f[service], [key]: val } }));
  const setPgUiField = (service, key, val) => setPgUi(f => ({ ...f, [service]: { ...f[service], [key]: val } }));
  const setPgFeeMethod = (service, method, field, val) => setPaymentGateways(f => ({
    ...f,
    [service]: {
      ...f[service],
      feesByMethod: { ...f[service].feesByMethod, [method]: { ...f[service].feesByMethod[method], [field]: val } },
    },
  }));

  // ── Which gateway each service pays through (2026-07-28) ──
  // The Online PG tabs now lead with a profile dropdown instead of assuming the legacy
  // per-service credentials below are the answer. mode: 'rules' | 'profile' | 'custom' —
  // the credential form only renders for 'custom'. See gatewayRouter.resolveForNewOrder.
  const [gatewayProfiles, setGatewayProfiles] = useState([]);
  const emptyRouting = { mode: 'rules', profile: null, splitThreshold: 1000, profileUpTo: null, profileAbove: null };
  const [serviceRouting, setServiceRouting] = useState({ catv: emptyRouting, broadband: emptyRouting });
  const [routingSaving, setRoutingSaving] = useState(null); // service currently saving
  const setRouting = (service, patch) =>
    setServiceRouting(f => ({ ...f, [service]: { ...f[service], ...patch } }));

  // Re-read profiles + choice after the amount-split editor creates or repoints profiles.
  const reloadGatewayProfiles = async () => {
    try {
      const { data } = await settingsAPI.getGatewayProfiles();
      setGatewayProfiles(data.data.profiles || []);
      if (data.data.serviceRouting) setServiceRouting(data.data.serviceRouting);
    } catch { /* the toast from the failed save already said enough */ }
  };

  // ── Payments tab sub-nav (CableTV/Broadband × Online PG/UPI QR) ──
  const [paymentsSubTab, setPaymentsSubTab] = useState('catv-pg');

  // ── Broadband UPI QR state ──
  const emptyBroadbandUpi = { payeeName: '', entries: [] };
  const [broadbandUpi, setBroadbandUpi] = useState(emptyBroadbandUpi);
  const [bbUpiSaving, setBbUpiSaving] = useState(false);

  // ── CableTV UPI QR state — separate entries from Broadband, alongside Online PG ──
  const emptyCatvUpi = { payeeName: '', entries: [] };
  const [catvUpi, setCatvUpi] = useState(emptyCatvUpi);
  const [catvUpiSaving, setCatvUpiSaving] = useState(false);

  // ── Netzur state ──
  const emptyImbs = { token: '', siteId: '', isEnabled: true, panelUsername: '', panelPassword: '' };
  const [imbs, setImbsState] = useState(emptyImbs);
  const [imbsSaving, setImbsSaving] = useState(false);
  const [imbsTesting, setImbsTesting] = useState(false);
  const [imbsTestResult, setImbsTestResult] = useState(null);
  const [imbsShowToken, setImbsShowToken] = useState(false);
  const [imbsShowPanelPw, setImbsShowPanelPw] = useState(false);
  const [imbsPanelTesting, setImbsPanelTesting] = useState(false);
  const setImbs = (key, val) => setImbsState(f => ({ ...f, [key]: val }));

  // ── PlayBoxTV state ──
  const emptyPlayboxtv = { apiKey: '', partnerKey: '', baseUrl: 'https://api.playboxtv.in/v5', tokenIss: '', tokenAud: '', isEnabled: true };
  const [playboxtv, setPlayboxtvState] = useState(emptyPlayboxtv);
  const [pbSaving, setPbSaving] = useState(false);
  const [pbTesting, setPbTesting] = useState(false);
  const [pbTestResult, setPbTestResult] = useState(null);
  const [pbShowApiKey, setPbShowApiKey] = useState(false);
  const [pbShowPartnerKey, setPbShowPartnerKey] = useState(false);
  const setPlayboxtv = (key, val) => setPlayboxtvState(f => ({ ...f, [key]: val }));

  // ── Load existing config ──
  useEffect(() => {
    (async () => {
      try {
        const [oltRes, billingRes, nasRes, pgCatvRes, pgBroadbandRes, bbUpiRes, catvUpiRes, imbsRes, pbRes, companyRes, receiptNumRes, profilesRes] = await Promise.allSettled([
          settingsAPI.getOLT(),
          settingsAPI.getBilling(),
          settingsAPI.getNASList(),
          settingsAPI.getPaymentGateway('catv'),
          settingsAPI.getPaymentGateway('broadband'),
          settingsAPI.getBroadbandUpi(),
          settingsAPI.getCatvUpi(),
          settingsAPI.getImbs(),
          settingsAPI.getPlayboxtv(),
          settingsAPI.getCompany(),
          settingsAPI.getReceiptNumbering(),
          settingsAPI.getGatewayProfiles(),
        ]);
        if (oltRes.status === 'fulfilled' && oltRes.value.data.data)
          setForm(f => ({ ...f, ...oltRes.value.data.data, password: MASKED }));
        if (billingRes.status === 'fulfilled' && billingRes.value.data.data)
          setBilling(billingRes.value.data.data);
        if (receiptNumRes.status === 'fulfilled' && receiptNumRes.value.data.data)
          setReceiptNum(receiptNumRes.value.data.data);
        if (nasRes.status === 'fulfilled' && nasRes.value.data.data)
          setNasList(nasRes.value.data.data);
        // feesByMethod needs its own shallow merge — a plain top-level spread would
        // wholesale-replace it with whatever (possibly absent/partial) shape the API
        // returns, losing the per-method default rows the UI needs to render.
        const mergeGateway = (apiData) => ({
          ...emptyPaymentGateway, ...apiData,
          feesByMethod: { ...emptyFeesByMethod, ...(apiData.feesByMethod || {}) },
        });
        if (pgCatvRes.status === 'fulfilled' && pgCatvRes.value.data.data)
          setPaymentGateways(f => ({ ...f, catv: mergeGateway(pgCatvRes.value.data.data) }));
        if (pgBroadbandRes.status === 'fulfilled' && pgBroadbandRes.value.data.data)
          setPaymentGateways(f => ({ ...f, broadband: mergeGateway(pgBroadbandRes.value.data.data) }));
        if (profilesRes.status === 'fulfilled' && profilesRes.value.data.data) {
          const { profiles, serviceRouting: sr } = profilesRes.value.data.data;
          setGatewayProfiles(profiles || []);
          if (sr) setServiceRouting(sr);
        }
        if (bbUpiRes.status === 'fulfilled' && bbUpiRes.value.data.data)
          setBroadbandUpi(f => ({ ...emptyBroadbandUpi, ...bbUpiRes.value.data.data }));
        if (catvUpiRes.status === 'fulfilled' && catvUpiRes.value.data.data)
          setCatvUpi(f => ({ ...emptyCatvUpi, ...catvUpiRes.value.data.data }));
        if (imbsRes.status === 'fulfilled' && imbsRes.value.data.data)
          setImbsState(f => ({ ...emptyImbs, ...imbsRes.value.data.data }));
        if (pbRes.status === 'fulfilled' && pbRes.value.data.data)
          setPlayboxtvState(f => ({ ...emptyPlayboxtv, ...pbRes.value.data.data }));
        if (companyRes.status === 'fulfilled' && companyRes.value.data.data)
          setCompany({
            catv:      { ...emptyCompanyDetail, ...companyRes.value.data.data.catv },
            broadband: { ...emptyCompanyDetail, ...companyRes.value.data.data.broadband },
          });
      } catch { /* no config yet */ }
      finally { setLoading(false); }
    })();
  }, []);

  // ── Save OLT ──
  const handleSave = async () => {
    if (!form.ip?.trim()) { toast.error('OLT IP address is required'); return; }
    setSaving(true);
    try {
      await settingsAPI.saveOLT(form);
      toast.success('OLT settings saved');
      setTestResult(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  // ── Save billing settings ──
  const handleSaveBilling = async () => {
    setBillingSaving(true);
    try {
      await settingsAPI.saveBilling(billing);
      toast.success('Billing settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setBillingSaving(false); }
  };

  // ── Save Receipt Numbering ──
  const handleSaveReceiptNumbering = async () => {
    const body = {};
    if (receiptNumForm.catvNext !== '') body.catvNext = receiptNumForm.catvNext;
    if (receiptNumForm.broadbandNext !== '') body.broadbandNext = receiptNumForm.broadbandNext;
    if (!Object.keys(body).length) { toast.error('Enter a starting number to change'); return; }
    setReceiptNumSaving(true);
    try {
      await settingsAPI.saveReceiptNumbering(body);
      const res = await settingsAPI.getReceiptNumbering();
      setReceiptNum(res.data.data);
      setReceiptNumForm({ catvNext: '', broadbandNext: '' });
      toast.success('Receipt numbering updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setReceiptNumSaving(false); }
  };

  // ── Save Company Details ──
  const handleSaveCompany = async () => {
    setCompanySaving(true);
    try {
      // Blank/whitespace-only lines from the textarea shouldn't become empty numbered points.
      const payload = {
        catv:      { ...company.catv,      termsAndConditions: company.catv.termsAndConditions.map(t => t.trim()).filter(Boolean) },
        broadband: { ...company.broadband, termsAndConditions: company.broadband.termsAndConditions.map(t => t.trim()).filter(Boolean) },
      };
      await settingsAPI.saveCompany(payload);
      toast.success('Company details saved');
    } catch (err) {
      // Surface the actual failure reason (network/timeout vs a real server error) instead of
      // a flat "Save failed" — logos are the one field here big enough to hit a slow-connection
      // timeout or an upstream proxy's body-size limit, and those look identical to the user
      // without this detail.
      const detail = err.response
        ? (err.response.data?.message || `Server returned ${err.response.status}`)
        : (err.code === 'ECONNABORTED' ? 'Request timed out — try a smaller logo image' : 'Network error — check your connection');
      toast.error(`Save failed: ${detail}`);
      console.error('Save company details failed:', err);
    } finally { setCompanySaving(false); }
  };

  // ── Save Payment Gateway settings ──
  const handleSavePaymentGateway = async (service) => {
    setPgUiField(service, 'saving', true);
    try {
      const res = await settingsAPI.savePaymentGateway(service, paymentGateways[service]);
      setPaymentGateways(f => ({
        ...f,
        [service]: {
          ...f[service], ...res.data.data,
          keySecret: res.data.data.keySecret ? MASKED : '',
          webhookSecret: res.data.data.webhookSecret ? MASKED : '',
          ablepaySalt: res.data.data.ablepaySalt ? MASKED : '',
        },
      }));
      toast.success(`${service === 'catv' ? 'CableTV' : 'Broadband'} payment gateway settings saved`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setPgUiField(service, 'saving', false); }
  };

  // ── Save which gateway a service pays through ──
  const handleSaveGatewayChoice = async (service) => {
    setRoutingSaving(service);
    try {
      const { mode, profile } = serviceRouting[service];
      const res = await settingsAPI.saveGatewayChoice(service, { mode, profile });
      if (res.data.data) setServiceRouting(res.data.data);
      toast.success(`${service === 'catv' ? 'CableTV' : 'Broadband'} gateway choice saved`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setRoutingSaving(null); }
  };

  const handleTestPaymentGateway = async (service) => {
    setPgUiField(service, 'testing', true);
    setPgUiField(service, 'testResult', null);
    try {
      const res = await settingsAPI.testPaymentGateway(service);
      setPgUiField(service, 'testResult', 'success');
      toast.success(res.data.message || 'Connected');
    } catch (err) {
      setPgUiField(service, 'testResult', 'failed');
      toast.error(err.response?.data?.message || 'Connection failed');
    } finally { setPgUiField(service, 'testing', false); }
  };

  // ── Broadband UPI QR entry list helpers ──
  const addUpiEntry = () => setBroadbandUpi(f => ({ ...f, entries: [...f.entries, { label: '', upiId: '' }] }));
  const removeUpiEntry = (i) => setBroadbandUpi(f => ({ ...f, entries: f.entries.filter((_, idx) => idx !== i) }));
  const setUpiEntry = (i, key, val) => setBroadbandUpi(f => ({
    ...f, entries: f.entries.map((e, idx) => idx === i ? { ...e, [key]: val } : e),
  }));

  // ── Save Broadband UPI settings ──
  const handleSaveBroadbandUpi = async () => {
    setBbUpiSaving(true);
    try {
      const res = await settingsAPI.saveBroadbandUpi(broadbandUpi);
      setBroadbandUpi(f => ({ ...emptyBroadbandUpi, ...res.data.data }));
      toast.success('Broadband UPI settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setBbUpiSaving(false); }
  };

  // ── CableTV UPI QR entry list helpers ──
  const addCatvUpiEntry = () => setCatvUpi(f => ({ ...f, entries: [...f.entries, { label: '', upiId: '' }] }));
  const removeCatvUpiEntry = (i) => setCatvUpi(f => ({ ...f, entries: f.entries.filter((_, idx) => idx !== i) }));
  const setCatvUpiEntry = (i, key, val) => setCatvUpi(f => ({
    ...f, entries: f.entries.map((e, idx) => idx === i ? { ...e, [key]: val } : e),
  }));

  // ── Save CableTV UPI settings ──
  const handleSaveCatvUpi = async () => {
    setCatvUpiSaving(true);
    try {
      const res = await settingsAPI.saveCatvUpi(catvUpi);
      setCatvUpi(f => ({ ...emptyCatvUpi, ...res.data.data }));
      toast.success('CableTV UPI settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setCatvUpiSaving(false); }
  };

  // ── Save Netzur settings ──
  const handleSaveImbs = async () => {
    setImbsSaving(true);
    try {
      const res = await settingsAPI.saveImbs(imbs);
      setImbsState(f => ({ ...f, ...res.data.data, token: res.data.data.token ? MASKED : '', panelPassword: f.panelPassword ? MASKED : '' }));
      toast.success('Netzur settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setImbsSaving(false); }
  };

  const handleTestImbs = async () => {
    setImbsTesting(true);
    setImbsTestResult(null);
    try {
      const res = await settingsAPI.testImbs();
      setImbsTestResult('success');
      toast.success(res.data.message || 'Connected');
    } catch (err) {
      setImbsTestResult('failed');
      toast.error(err.response?.data?.message || 'Connection failed');
    } finally { setImbsTesting(false); }
  };

  const handleTestImbsPanel = async () => {
    setImbsPanelTesting(true);
    try {
      const res = await settingsAPI.testImbsPanel();
      toast.success(res.data.message || 'Panel login works');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Panel login failed');
    } finally { setImbsPanelTesting(false); }
  };

  // ── Save PlayBoxTV settings ──
  const handleSavePlayboxtv = async () => {
    setPbSaving(true);
    try {
      const res = await settingsAPI.savePlayboxtv(playboxtv);
      setPlayboxtvState(f => ({
        ...f, ...res.data.data,
        apiKey: res.data.data.apiKey ? MASKED : '',
        partnerKey: res.data.data.partnerKey ? MASKED : '',
      }));
      toast.success('PlayBoxTV settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setPbSaving(false); }
  };

  const handleTestPlayboxtv = async () => {
    setPbTesting(true);
    setPbTestResult(null);
    try {
      const res = await settingsAPI.testPlayboxtv();
      setPbTestResult('success');
      toast.success(res.data.message || 'Connected');
    } catch (err) {
      setPbTestResult('failed');
      toast.error(err.response?.data?.message || 'Connection failed');
    } finally { setPbTesting(false); }
  };

  // ── Test OLT connection ──
  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await settingsAPI.testOLTConnection();
      setTestResult('success');
      toast.success(res.data.message || 'Connection successful');
    } catch (err) {
      setTestResult('failed');
      toast.error(err.response?.data?.message || 'Connection failed');
    } finally { setTesting(false); }
  };

  // ── PON port helpers ──
  const addPort = () => setForm(f => ({
    ...f,
    ponPorts: [...f.ponPorts, { slot: 0, port: f.ponPorts.length, name: '', description: '', isActive: true }]
  }));
  const removePort = (i) => setForm(f => ({ ...f, ponPorts: f.ponPorts.filter((_, idx) => idx !== i) }));
  const setPort = (i, key, val) => setForm(f => {
    const pts = [...f.ponPorts];
    pts[i] = { ...pts[i], [key]: key === 'isActive' ? val : (key === 'slot' || key === 'port' ? parseInt(val) || 0 : val) };
    return { ...f, ponPorts: pts };
  });

  // ── GE (uplink) port helpers ──
  // Ports are auto-detected live from the OLT (below), not manually added — this
  // just holds a name against whichever port id the admin types one for.
  const [liveGePorts, setLiveGePorts] = useState([]);
  const [geLoading, setGeLoading] = useState(false);
  const loadLiveGePorts = useCallback(async () => {
    setGeLoading(true);
    try {
      const res = await oltAPI.getGEPorts();
      setLiveGePorts(res.data.data || []);
    } catch { /* OLT may be unreachable — saved names are still editable below */ }
    finally { setGeLoading(false); }
  }, []);
  const setGeName = (id, name) => setForm(f => {
    const rest = f.gePorts.filter(p => p.id !== id);
    return { ...f, gePorts: name ? [...rest, { id, name }] : rest };
  });
  useEffect(() => {
    if (tab === 'ge-ports' && liveGePorts.length === 0 && !geLoading) loadLiveGePorts();
  }, [tab, liveGePorts.length, geLoading, loadLiveGePorts]);

  // ── NAS handlers ──
  const handleNasSave = async () => {
    if (!nasForm.name.trim()) { toast.error('NAS name is required'); return; }
    if (!nasForm.ip.trim())   { toast.error('IP address is required'); return; }
    setNasSaving(true);
    try {
      if (editingNasId) {
        const res = await settingsAPI.updateNAS(editingNasId, nasForm);
        setNasList(l => l.map(n => n._id === editingNasId ? res.data.data : n));
        toast.success('NAS updated');
      } else {
        const res = await settingsAPI.addNAS(nasForm);
        setNasList(l => [...l, res.data.data]);
        toast.success('NAS added');
      }
      setShowNasForm(false);
      setNasForm(emptyNAS);
      setEditingNasId(null);
      setNasDiscoveredIfaces([]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setNasSaving(false); }
  };

  const handleNasEdit = (nas) => {
    setNasForm({
      name: nas.name,
      ip: nas.ip,
      port: nas.port,
      restPort: nas.restPort || (nas.useSSL ? 443 : 80),
      username: nas.username || '',
      password: NAS_MASKED,
      useSSL: !!nas.useSSL,
      apiMode: nas.apiMode || 'rest',
      isEnabled: nas.isEnabled !== false,
      wanInterfaces: nas.wanInterfaces || [],
    });
    setEditingNasId(nas._id);
    setShowNasForm(true);
    setNasShowPass(false);
    setNasDiscoveredIfaces([]);
  };

  const handleNasDelete = async (id) => {
    if (!window.confirm('Delete this NAS?')) return;
    setNasDeleting(id);
    try {
      await settingsAPI.deleteNAS(id);
      setNasList(l => l.filter(n => n._id !== id));
      toast.success('NAS deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally { setNasDeleting(null); }
  };

  const handleNasTest = async (id) => {
    setNasTesting(id);
    try {
      const res = await settingsAPI.testNAS(id);
      setNasList(l => l.map(n => n._id === id ? { ...n, lastTestResult: 'success', lastTestedAt: new Date().toISOString() } : n));
      toast.success(res.data.message || 'Connected');
    } catch (err) {
      setNasList(l => l.map(n => n._id === id ? { ...n, lastTestResult: 'failed', lastTestedAt: new Date().toISOString() } : n));
      toast.error(err.response?.data?.message || 'Connection failed');
    } finally { setNasTesting(null); }
  };

  const handleDiscoverInterfaces = async () => {
    if (!editingNasId) { toast.error('Save the NAS first, then discover interfaces'); return; }
    setNasDiscovering(true);
    try {
      const res = await settingsAPI.getNASInterfaces(editingNasId);
      setNasDiscoveredIfaces(res.data.data || []);
      if ((res.data.data || []).length === 0) toast('No WAN interfaces found on this NAS');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to discover interfaces');
    } finally { setNasDiscovering(false); }
  };

  const toggleWanIface = (name) => {
    setNasForm(f => {
      const ifaces = f.wanInterfaces || [];
      return {
        ...f,
        wanInterfaces: ifaces.includes(name) ? ifaces.filter(n => n !== name) : [...ifaces, name],
      };
    });
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
      <div className="spinner" style={{ margin: '0 auto 12px' }} />Loading settings…
    </div>
  );

  const tabGroups = [
    {
      label: 'Billing & Company', items: [
        { key: 'billing', label: 'Billing', icon: Receipt },
        { key: 'company', label: 'Company Details', icon: Building2 },
      ],
    },
    {
      label: 'OLT / Network', items: [
        { key: 'connection', label: 'OLT Connection', icon: Router },
        { key: 'ports',      label: 'PON Ports', icon: Wifi },
        { key: 'ge-ports',   label: 'GE (Uplink) Ports', icon: Activity },
        { key: 'power',      label: 'Power Thresholds', icon: AlertTriangle },
        { key: 'nas',        label: 'NAS Devices', icon: Server },
      ],
    },
    {
      label: 'Integrations', items: [
        { key: 'sms',      label: 'SMS Gateway', icon: MessagesSquare },
        { key: 'payments', label: 'Payment (Gateway / QR)', icon: CreditCard },
        { key: 'imbs',     label: 'Netzur Integration', icon: Cable },
        { key: 'playboxtv', label: 'PlayBoxTV Integration', icon: Tv },
        { key: 'googleSheets', label: 'Google Sheets Sync', icon: FileSpreadsheet },
      ],
    },
    {
      label: 'Data & Records', items: [
        { key: 'ads',            label: 'App Advertisement', icon: ImageIcon },
        { key: 'import',         label: 'Import Data', icon: Upload },
        { key: 'history-import', label: 'Payment History Import — CableTV', icon: FileClock },
        { key: 'history-import-broadband', label: 'Payment History Import — Broadband', icon: FileClock },
      ],
    },
    {
      label: 'App View', items: [
        { key: 'app-view', label: 'Bottom Tabs & Menu', icon: Smartphone },
        { key: 'dashboard-widgets', label: 'Dashboard Widgets', icon: LayoutDashboard },
      ],
    },
  ];

  const oltButtons = (
    <div style={{ display: 'flex', gap: 8 }}>
      <button
        className="btn btn-secondary btn-sm"
        onClick={handleTest}
        disabled={testing || !form.ip}
      >
        {testing
          ? <><Loader size={12} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} />Testing…</>
          : testResult === 'success'
            ? <><CheckCircle size={12} style={{ marginRight: 5, color: 'var(--success)' }} />Connected</>
            : testResult === 'failed'
              ? <><XCircle size={12} style={{ marginRight: 5, color: 'var(--danger)' }} />Failed</>
              : <><RefreshCw size={12} style={{ marginRight: 5 }} />Test Connection</>
        }
      </button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
        <Save size={12} style={{ marginRight: 5 }} />
        {saving ? 'Saving…' : 'Save Settings'}
      </button>
    </div>
  );

  // ── App view home: search + grouped list, one tap into a setting ──
  const allItems = tabGroups.flatMap(g => g.items.map(i => ({ ...i, group: g.label })));
  const q = appSearch.trim().toLowerCase();
  const matches = q
    ? allItems.filter(i => `${i.label} ${i.group} ${(APP_INFO[i.key] || []).join(' ')}`.toLowerCase().includes(q))
    : null;
  const openTab = (key) => { setTab(key); setAppOpen(true); window.scrollTo(0, 0); };
  const menuRow = ({ key, label, icon: Icon }) => (
    <button
      key={key}
      type="button"
      onClick={() => openTab(key)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12, minHeight: 56, padding: '8px 14px',
        border: 'none', borderBottom: '1px solid var(--border)', background: 'none', textAlign: 'left',
        cursor: 'pointer', color: 'var(--text)',
      }}
    >
      <Icon size={20} color="var(--primary)" style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 600, fontSize: '.9rem' }}>{label}</span>
        <span style={{ display: 'block', fontSize: '.74rem', color: 'var(--text-muted)', lineHeight: 1.35 }}>
          {(APP_INFO[key] || [])[0]}
        </span>
      </span>
      <ChevronRight size={18} color="var(--text-muted)" />
    </button>
  );

  if (isApp && !appOpen) return (
    <div className="settings-container">
      <input
        className="form-control"
        type="search"
        value={appSearch}
        onChange={e => setAppSearch(e.target.value)}
        placeholder="Search settings (gst, logo, upi, olt…)"
        style={{ marginBottom: 12, minHeight: 44 }}
      />
      {(matches ? [{ label: `${matches.length} found`, items: matches }] : tabGroups).map(g => (
        <div key={g.label} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '0 4px 6px' }}>{g.label}</div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {g.items.map(menuRow)}
            {g.items.length === 0 && <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: '.85rem' }}>Nothing matches.</div>}
          </div>
        </div>
      ))}
    </div>
  );

  const current = allItems.find(i => i.key === tab);

  return (
    <div className="settings-container" style={{ maxWidth: 1120, margin: '0 auto' }}>

      {/* ── App view: Back bar (sticks to the top of the scroller) ── */}
      {isApp && (
        <div style={{
          position: 'sticky', top: -14, zIndex: 5, margin: '-14px -14px 12px', padding: '8px 14px',
          background: 'var(--bg)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <button
            type="button"
            onClick={() => setAppOpen(false)}
            aria-label="Back to Settings"
            style={{ width: 44, height: 44, border: 'none', background: 'none', color: 'var(--text)', display: 'grid', placeItems: 'center', cursor: 'pointer', marginLeft: -10 }}
          >
            <ArrowLeft size={20} />
          </button>
          <span style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: '.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {current?.label || 'Settings'}
          </span>
        </div>
      )}
      {isApp && OLT_TABS.includes(tab) && <div style={{ marginBottom: 12 }}>{oltButtons}</div>}

      {/* ── Page header ── */}
      {!isApp && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Settings size={18} /> Settings
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '.83rem', margin: '2px 0 0' }}>
              OLT configuration — admin only
            </p>
          </div>
          {oltButtons}
        </div>
      )}

      <div className="settings-shell">
        {/* ── Grouped vertical nav ── */}
        {!isApp && <nav className="settings-nav">
          {tabGroups.map(group => (
            <div className="settings-nav-group" key={group.label}>
              <div className="settings-nav-group-label">{group.label}</div>
              {group.items.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`settings-nav-item ${tab === key ? 'active' : ''}`}
                >
                  <Icon size={15} />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>}

        <div className="settings-content">

      {/* ── Billing Tab ── */}
      {tab === 'billing' && (
        <div className="card">
          <div className="card-body">
            <SECTION title="GST / Tax">
              <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', fontSize: '.95rem', marginBottom: 20, padding: '14px 16px', background: billing.gstEnabled ? 'var(--cb-f0fdf4)' : 'var(--cb-f8fafc)', border: `1px solid ${billing.gstEnabled ? 'var(--cb-bbf7d0)' : 'var(--border)'}`, borderRadius: 8 }}>
                <input
                  type="checkbox"
                  checked={billing.gstEnabled}
                  onChange={e => setBilling(b => ({ ...b, gstEnabled: e.target.checked }))}
                  style={{ width: 18, height: 18, accentColor: 'var(--success)', cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>Enable GST on payments</div>
                  <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    {billing.gstEnabled
                      ? 'GST is active — all payments will include GST and receipts will show the GST breakdown.'
                      : 'GST is disabled — all payments will be collected at face value, no GST added.'}
                  </div>
                </div>
              </label>

              {billing.gstEnabled && (
                <FIELD label="Default GST %" hint="Pre-selected when recording a payment. Staff can change it per transaction.">
                  <select
                    className="form-control"
                    style={{ maxWidth: 200 }}
                    value={billing.defaultGstPercent}
                    onChange={e => setBilling(b => ({ ...b, defaultGstPercent: parseInt(e.target.value) }))}
                  >
                    <option value={5}>5%</option>
                    <option value={12}>12%</option>
                    <option value={18}>18%</option>
                  </select>
                </FIELD>
              )}
            </SECTION>

            <SECTION title="WhatsApp Templates">
              <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>
                Renewal reminder templates have moved to{' '}
                <Link to="/messaging" style={{ color: 'var(--primary)', fontWeight: 600 }}>Message Center → Customer Notify</Link>,
                alongside the reminder schedule that uses them. Alert recipients — who gets a WhatsApp when a PON
                port goes down, an ONU loses signal, a complaint comes in, or a payment is collected — are in{' '}
                <Link to="/messaging" style={{ color: 'var(--primary)', fontWeight: 600 }}>Message Center → Alerts</Link>.
              </p>
            </SECTION>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-primary" onClick={handleSaveBilling} disabled={billingSaving}>
                <Save size={14} style={{ marginRight: 6 }} />
                {billingSaving ? 'Saving…' : 'Save Billing Settings'}
              </button>
            </div>

            <SECTION title="Receipt Numbering">
              <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
                CATV receipts: <code style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3 }}>LCV{'<n>'}</code> (never resets).
                Broadband receipts: <code style={{ background: 'var(--bg)', padding: '1px 5px', borderRadius: 3 }}>{'<FY>'}/{'<n>'}</code> — the sequence
                automatically restarts at 1 each new financial year (1 April).
              </p>

              <div className="set-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 16 }}>
                <div style={{ padding: '12px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Next CATV Receipt</div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem', marginTop: 2 }}>LCV{receiptNum.catv?.nextNumber}</div>
                </div>
                <div style={{ padding: '12px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Next Broadband Receipt (FY {receiptNum.broadband?.fyLabel})</div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem', marginTop: 2 }}>{receiptNum.broadband?.fyLabel}/{receiptNum.broadband?.nextNumber}</div>
                </div>
              </div>

              <div className="set-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <FIELD label="Set next CATV number to" hint="Leave blank to keep it unchanged.">
                  <input className="form-control" type="number" min="1" placeholder={`e.g. ${receiptNum.catv?.nextNumber}`}
                    value={receiptNumForm.catvNext} onChange={e => setReceiptNumForm(f => ({ ...f, catvNext: e.target.value }))} />
                </FIELD>
                <FIELD label={`Set next Broadband number to (FY ${receiptNum.broadband?.fyLabel})`} hint="Leave blank to keep it unchanged.">
                  <input className="form-control" type="number" min="1" placeholder={`e.g. ${receiptNum.broadband?.nextNumber}`}
                    value={receiptNumForm.broadbandNext} onChange={e => setReceiptNumForm(f => ({ ...f, broadbandNext: e.target.value }))} />
                </FIELD>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleSaveReceiptNumbering} disabled={receiptNumSaving}>
                  <Save size={12} style={{ marginRight: 4 }} />
                  {receiptNumSaving ? 'Saving…' : 'Update Numbering'}
                </button>
              </div>
            </SECTION>
          </div>
        </div>
      )}

      {/* ── Company Details Tab ── */}
      {tab === 'company' && (
        <div className="card">
          <div className="card-body">
            <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', marginTop: 0, marginBottom: 20 }}>
              These details print on receipts and WhatsApp invoices — CableTV and Broadband are billed as
              separate businesses here, so each gets its own name, address, GSTIN, and UPI details.
            </p>

            {[
              { type: 'catv',      title: 'CableTV — Company Details' },
              { type: 'broadband', title: 'Broadband — Company Details' },
            ].map(({ type, title }) => (
              <SECTION key={type} title={title}>
                <FIELD label="Logo" hint="Any image, any size — automatically resized on upload. Printed on receipts and invoices in place of the initials placeholder">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: 8, border: '1px dashed var(--border)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      overflow: 'hidden', background: 'var(--cb-f9fafb)', flexShrink: 0,
                    }}>
                      {company[type].logo
                        ? <img src={company[type].logo} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        : <ImageIcon size={22} color="var(--text-muted)" />}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', width: 'fit-content', margin: 0 }}>
                        <Upload size={13} style={{ marginRight: 5 }} />{company[type].logo ? 'Replace Logo' : 'Upload Logo'}
                        <input type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={e => { handleLogoUpload(type, e.target.files?.[0]); e.target.value = ''; }} />
                      </label>
                      {company[type].logo && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCompanyField(type, 'logo', '')}>
                          <X size={13} style={{ marginRight: 5 }} />Remove
                        </button>
                      )}
                    </div>
                  </div>
                </FIELD>
                <div className="set-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <FIELD label="Company Name">
                    <input className="form-control" value={company[type].name}
                      onChange={e => setCompanyField(type, 'name', e.target.value)}
                      placeholder="e.g. Laxmi Cable Vision" />
                  </FIELD>
                  <FIELD label="Phone">
                    <input className="form-control" value={company[type].phone}
                      onChange={e => setCompanyField(type, 'phone', e.target.value)} />
                  </FIELD>
                  <FIELD label="Email">
                    <input className="form-control" type="email" value={company[type].email}
                      onChange={e => setCompanyField(type, 'email', e.target.value)}
                      placeholder="e.g. info@example.com" />
                  </FIELD>
                  <FIELD label="Address" hint="Shown on printed receipts">
                    <input className="form-control" value={company[type].address}
                      onChange={e => setCompanyField(type, 'address', e.target.value)} />
                  </FIELD>
                  <FIELD label="GSTIN">
                    <input className="form-control" value={company[type].gstin}
                      onChange={e => setCompanyField(type, 'gstin', e.target.value)} />
                  </FIELD>
                  <FIELD label="UPI ID">
                    <input className="form-control" value={company[type].upiId}
                      onChange={e => setCompanyField(type, 'upiId', e.target.value)}
                      placeholder="e.g. business@upi" />
                  </FIELD>
                  <FIELD label="UPI Payee Name">
                    <input className="form-control" value={company[type].upiName}
                      onChange={e => setCompanyField(type, 'upiName', e.target.value)} />
                  </FIELD>
                </div>
                <FIELD label="Terms & Conditions" hint="One point per line, printed numbered on the invoice below the bill total. Use {{phone}} to insert the phone number above. Leave blank to use the default terms.">
                  <textarea className="form-control" rows={6}
                    value={company[type].termsAndConditions.join('\n')}
                    onChange={e => setCompanyField(type, 'termsAndConditions', e.target.value.split('\n'))}
                    placeholder="e.g. Recharges are non-transferable & non-refundable." />
                </FIELD>
              </SECTION>
            ))}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-primary" onClick={handleSaveCompany} disabled={companySaving}>
                <Save size={14} style={{ marginRight: 6 }} />
                {companySaving ? 'Saving…' : 'Save Company Details'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Connection Tab ── */}
      {tab === 'connection' && (
        <div className="card">
          <div className="card-body">

            <SECTION title="Device Identity">
              <div className="form-grid">
                <FIELD label="Make / Vendor">
                  <input className="form-control" value={form.make}
                    onChange={e => set('make', e.target.value)} placeholder="e.g. SYROTECH" />
                </FIELD>
                <FIELD label="Model">
                  <input className="form-control" value={form.model}
                    onChange={e => set('model', e.target.value)} placeholder="e.g. SY-GPON-1040" />
                </FIELD>
              </div>
              <div className="form-grid">
                <FIELD label="Device Label / Name" hint="Friendly name shown in the CRM">
                  <input className="form-control" value={form.deviceName}
                    onChange={e => set('deviceName', e.target.value)} placeholder="e.g. Main OLT" />
                </FIELD>
                <FIELD label="Location">
                  <input className="form-control" value={form.location}
                    onChange={e => set('location', e.target.value)} placeholder="e.g. Server Room, Floor 1" />
                </FIELD>
              </div>
              <FIELD label="Firmware Version" hint="Optional — for reference only">
                <input className="form-control" value={form.firmwareVersion}
                  onChange={e => set('firmwareVersion', e.target.value)} placeholder="e.g. V1.2.3" style={{ maxWidth: 280 }} />
              </FIELD>
            </SECTION>

            <SECTION title="Network Connection">
              <div className="form-grid">
                <FIELD label="Static IP Address" required hint="OLT management interface IP on your LAN">
                  <input className="form-control font-mono" value={form.ip}
                    onChange={e => set('ip', e.target.value.trim())} placeholder="e.g. 192.168.1.100" />
                </FIELD>
                <FIELD label="Port" hint={form.useSSL ? 'HTTPS port (usually 443 or 8443)' : 'HTTP port (usually 80 or 8080)'}>
                  <input className="form-control" type="number" min={1} max={65535}
                    value={form.port} onChange={e => set('port', parseInt(e.target.value) || (form.useSSL ? 443 : 80))} />
                </FIELD>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem', marginTop: 12 }}>
                <input type="checkbox" checked={!!form.useSSL}
                  onChange={e => {
                    const ssl = e.target.checked;
                    set('useSSL', ssl);
                    if (ssl && (form.port === 80 || !form.port)) set('port', 443);
                    if (!ssl && (form.port === 443)) set('port', 80);
                  }} />
                Use HTTPS / SSL
                <span style={{ color: 'var(--text-muted)', fontSize: '.8rem', fontWeight: 400 }}>
                  — enable if the OLT resets the connection ("socket hang up" error)
                </span>
              </label>
            </SECTION>

            <SECTION title="Authentication">
              <div className="form-grid">
                <FIELD label="Username" required>
                  <input className="form-control" value={form.username}
                    onChange={e => set('username', e.target.value)} placeholder="admin" />
                </FIELD>
                <FIELD label="Password" hint={form.password === MASKED ? 'Leave unchanged to keep current password' : ''}>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="form-control"
                      type={showPass ? 'text' : 'password'}
                      value={form.password}
                      onFocus={() => { if (form.password === MASKED) set('password', ''); }}
                      onBlur={() => { if (form.password === '') set('password', MASKED); }}
                      onChange={e => set('password', e.target.value)}
                      style={{ paddingRight: 40 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(s => !s)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}
                    >
                      {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </FIELD>
              </div>
            </SECTION>

            <SECTION title="Status">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem' }}>
                <input type="checkbox" checked={form.isEnabled}
                  onChange={e => set('isEnabled', e.target.checked)} />
                Enable OLT integration (uncheck to disable all OLT API calls)
              </label>
            </SECTION>

          </div>
        </div>
      )}

      {/* ── PON Ports Tab ── */}
      {tab === 'ports' && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '.95rem' }}>PON Port Configuration</h3>
            <button className="btn btn-primary btn-sm" onClick={addPort}>
              <Plus size={13} style={{ marginRight: 5 }} />Add Port
            </button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {form.ponPorts.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                <Wifi size={32} style={{ marginBottom: 8, opacity: .4 }} />
                <div style={{ fontSize: '.88rem' }}>No PON ports configured yet.</div>
                <div style={{ fontSize: '.8rem', marginTop: 4 }}>Click "Add Port" to define your OLT's PON ports.</div>
              </div>
            ) : (
              <div style={{ padding: '0 20px 20px' }}>
                {/* Column headers */}
                <div className="set-tbl-pon" style={{ display: 'grid', gridTemplateColumns: '60px 80px 1fr 1fr 80px 40px', gap: 10, padding: '10px 0 6px', fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  <span>Slot</span><span>Port #</span><span>Label</span><span>Description</span><span>Active</span><span></span>
                </div>
                {form.ponPorts.map((p, i) => (
                  <div key={i} className="set-tbl-pon" style={{ display: 'grid', gridTemplateColumns: '60px 80px 1fr 1fr 80px 40px', gap: 10, alignItems: 'center', padding: '6px 0', borderTop: '1px solid var(--border)' }}>
                    <input className="form-control" type="number" min={0} value={p.slot}
                      onChange={e => setPort(i, 'slot', e.target.value)} style={{ textAlign: 'center', padding: '5px 8px' }} />
                    <input className="form-control" type="number" min={0} value={p.port}
                      onChange={e => setPort(i, 'port', e.target.value)} style={{ textAlign: 'center', padding: '5px 8px' }} />
                    <input className="form-control" value={p.name}
                      onChange={e => setPort(i, 'name', e.target.value)} placeholder={`PON ${i + 1}`} style={{ padding: '5px 10px' }} />
                    <input className="form-control" value={p.description}
                      onChange={e => setPort(i, 'description', e.target.value)} placeholder="Optional note" style={{ padding: '5px 10px' }} />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <input type="checkbox" checked={p.isActive} onChange={e => setPort(i, 'isActive', e.target.checked)} style={{ width: 16, height: 16 }} />
                    </div>
                    <button className="btn btn-danger btn-sm" onClick={() => removePort(i)} style={{ padding: '5px 8px' }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── GE (Uplink) Ports Tab ── */}
      {tab === 'ge-ports' && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '.95rem' }}>GE (Uplink) Port Names</h3>
            <button className="btn btn-outline btn-sm" onClick={loadLiveGePorts} disabled={geLoading}>
              {geLoading
                ? <Loader size={12} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} />
                : <RefreshCw size={12} style={{ marginRight: 5 }} />}
              Refresh
            </button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <p style={{ padding: '14px 20px 0', color: 'var(--text-muted)', fontSize: '.82rem', lineHeight: 1.6 }}>
              Name the OLT's GE ports (e.g. "ISP1 Primary", "ISP2 Backup") so uplink-down
              WhatsApp alerts show something more useful than a raw port id.
            </p>
            {(() => {
              const ids = new Set([...liveGePorts.map(p => p.id), ...form.gePorts.map(p => p.id)]);
              const sorted = [...ids].sort((a, b) => (parseInt(a.replace(/\D/g, ''), 10) || 0) - (parseInt(b.replace(/\D/g, ''), 10) || 0));
              if (sorted.length === 0) {
                return (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                    <Activity size={32} style={{ marginBottom: 8, opacity: .4 }} />
                    <div style={{ fontSize: '.88rem' }}>
                      {geLoading ? 'Loading GE ports from the OLT…' : 'No GE ports detected — check the OLT connection, or click Refresh.'}
                    </div>
                  </div>
                );
              }
              return (
                <div style={{ padding: '14px 20px 20px' }}>
                  <div className="set-tbl-ge" style={{ display: 'grid', gridTemplateColumns: '90px 90px 1fr', gap: 10, padding: '10px 0 6px', fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    <span>Port</span><span>Status</span><span>Name</span>
                  </div>
                  {sorted.map(id => {
                    const live = liveGePorts.find(p => p.id === id);
                    const saved = form.gePorts.find(p => p.id === id);
                    return (
                      <div key={id} className="set-tbl-ge" style={{ display: 'grid', gridTemplateColumns: '90px 90px 1fr', gap: 10, alignItems: 'center', padding: '6px 0', borderTop: '1px solid var(--border)' }}>
                        <b style={{ fontSize: '.85rem' }}>{id}</b>
                        <span style={{ fontSize: '.78rem', color: live?.status === 'up' ? 'var(--c-22c55e)' : 'var(--text-muted)' }}>
                          {live ? (live.status === 'up' ? 'Up' : 'Down') : '—'}
                        </span>
                        <input className="form-control" value={saved?.name || ''}
                          onChange={e => setGeName(id, e.target.value)}
                          placeholder="e.g. ISP1 Primary" style={{ padding: '5px 10px' }} />
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Power Thresholds Tab ── */}
      {tab === 'power' && (
        <div className="card">
          <div className="card-body">
            <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 20, lineHeight: 1.6 }}>
              These thresholds are used to colour-code signal strength badges on the OLT management page.
              All values are in <strong>dBm</strong>. Typical GPON Rx range: −8 to −30 dBm.
            </p>

            <SECTION title="Receive Power (Rx) — ONU optical input">
              <PowerBar
                min={form.power.minRxPower}
                max={form.power.maxRxPower}
                goodMin={form.power.goodRxMin}
                goodMax={form.power.goodRxMax}
              />
              <div className="form-grid" style={{ marginTop: 16 }}>
                <FIELD label="Min Rx Power (dBm)" hint="Below this = critical / weak signal">
                  <input className="form-control" type="number" step="0.5"
                    value={form.power.minRxPower} onChange={e => setPow('minRxPower', e.target.value)} />
                </FIELD>
                <FIELD label="Max Rx Power (dBm)" hint="Above this = saturated / too strong">
                  <input className="form-control" type="number" step="0.5"
                    value={form.power.maxRxPower} onChange={e => setPow('maxRxPower', e.target.value)} />
                </FIELD>
              </div>
              <div className="form-grid">
                <FIELD label="Good Range — Lower (dBm)" hint="Start of acceptable signal range">
                  <input className="form-control" type="number" step="0.5"
                    value={form.power.goodRxMin} onChange={e => setPow('goodRxMin', e.target.value)} />
                </FIELD>
                <FIELD label="Good Range — Upper (dBm)" hint="End of acceptable signal range">
                  <input className="form-control" type="number" step="0.5"
                    value={form.power.goodRxMax} onChange={e => setPow('goodRxMax', e.target.value)} />
                </FIELD>
              </div>
            </SECTION>

            <SECTION title="Transmit Power (Tx) — ONU optical output">
              <div className="form-grid">
                <FIELD label="Min Tx Power (dBm)">
                  <input className="form-control" type="number" step="0.5"
                    value={form.power.minTxPower} onChange={e => setPow('minTxPower', e.target.value)} />
                </FIELD>
                <FIELD label="Max Tx Power (dBm)">
                  <input className="form-control" type="number" step="0.5"
                    value={form.power.maxTxPower} onChange={e => setPow('maxTxPower', e.target.value)} />
                </FIELD>
              </div>

              {/* Reference table */}
              <div style={{ marginTop: 8, background: 'var(--bg)', borderRadius: 8, padding: 14, fontSize: '.8rem' }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '.72rem', letterSpacing: '.05em' }}>
                  Typical GPON signal reference
                </div>
                <div className="set-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
                  {[
                    ['−8 to −12 dBm', 'Excellent (very close ONU)'],
                    ['−12 to −22 dBm', 'Good'],
                    ['−22 to −27 dBm', 'Fair'],
                    ['Below −27 dBm', 'Weak — investigate fiber'],
                    ['Below −30 dBm', 'Critical — likely fault'],
                    ['Above −8 dBm', 'Too strong — add attenuator'],
                  ].map(([range, label]) => (
                    <div key={range} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span className="font-mono" style={{ color: 'var(--text)', fontWeight: 500 }}>{range}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </SECTION>

          </div>
        </div>
      )}

      {/* ── NAS Devices Tab ── */}
      {tab === 'nas' && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '.95rem' }}>NAS Devices</h3>
            <button className="btn btn-primary btn-sm" onClick={() => { setNasForm(emptyNAS); setEditingNasId(null); setShowNasForm(true); setNasShowPass(false); setNasDiscoveredIfaces([]); }}>
              <Plus size={13} style={{ marginRight: 5 }} />Add NAS
            </button>
          </div>

          {/* Add / Edit form */}
          {showNasForm && (
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
              <div style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 12 }}>
                {editingNasId ? 'Edit NAS' : 'New NAS'}
              </div>
              <div className="form-grid">
                <FIELD label="Name" required>
                  <input className="form-control" value={nasForm.name} onChange={e => setNas('name', e.target.value)} placeholder="e.g. NAS 1 (AIS)" />
                </FIELD>
                <FIELD label="IP Address" required>
                  <input className="form-control font-mono" value={nasForm.ip} onChange={e => setNas('ip', e.target.value.trim())} placeholder="e.g. 103.145.9.238" />
                </FIELD>
              </div>
              <FIELD label="API Mode" hint={nasForm.apiMode === 'legacy'
                ? 'For RouterOS builds older than 7.1 (no REST API) — polled via the classic binary API on the RADIUS/API port below. Needs that port allowed through the router\'s firewall/API service for this server\'s IP.'
                : 'For RouterOS 7.1+ — polled over HTTP(S) REST on the REST API port below.'}>
                <select className="form-control" value={nasForm.apiMode || 'rest'} onChange={e => setNas('apiMode', e.target.value)} style={{ maxWidth: 320 }}>
                  <option value="rest">REST API (RouterOS 7.1+)</option>
                  <option value="legacy">Legacy Binary API (RouterOS pre-7.1)</option>
                </select>
              </FIELD>
              <div className="form-grid">
                <FIELD label="RADIUS / API Port" hint={nasForm.apiMode === 'legacy' ? 'Used to connect — Mikrotik API port, usually 8728' : 'Mikrotik API port — usually 8628'}>
                  <input className="form-control" type="number" min={1} max={65535} value={nasForm.port} onChange={e => setNas('port', parseInt(e.target.value) || 8628)} />
                </FIELD>
                {nasForm.apiMode !== 'legacy' && (
                  <FIELD label="REST API Port" hint="Mikrotik REST port — 80 for HTTP (www), 443 for HTTPS (www-ssl)">
                    <input className="form-control" type="number" min={1} max={65535}
                      value={nasForm.restPort || (nasForm.useSSL ? 443 : 80)}
                      onChange={e => setNas('restPort', parseInt(e.target.value) || 80)} />
                  </FIELD>
                )}
                <FIELD label="Username">
                  <input className="form-control" value={nasForm.username} onChange={e => setNas('username', e.target.value)} placeholder="api" />
                </FIELD>
              </div>
              <FIELD label="Password" hint={nasForm.password === NAS_MASKED ? 'Leave unchanged to keep current password' : ''}>
                <div style={{ position: 'relative', maxWidth: 320 }}>
                  <input
                    className="form-control"
                    type={nasShowPass ? 'text' : 'password'}
                    value={nasForm.password}
                    onFocus={() => { if (nasForm.password === NAS_MASKED) setNas('password', ''); }}
                    onBlur={() => { if (nasForm.password === '') setNas('password', NAS_MASKED); }}
                    onChange={e => setNas('password', e.target.value)}
                    style={{ paddingRight: 40 }}
                  />
                  <button type="button" onClick={() => setNasShowPass(s => !s)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                    {nasShowPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </FIELD>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
                {nasForm.apiMode !== 'legacy' && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.88rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!nasForm.useSSL} onChange={e => setNas('useSSL', e.target.checked)} /> Use HTTPS
                  </label>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.88rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={nasForm.isEnabled !== false} onChange={e => setNas('isEnabled', e.target.checked)} /> Enabled
                </label>
              </div>

              {/* WAN Interface picker */}
              {editingNasId && (
                <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--card-bg, #fff)', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                      WAN Interfaces
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handleDiscoverInterfaces}
                      disabled={nasDiscovering}
                      style={{ fontSize: '.75rem' }}
                    >
                      {nasDiscovering
                        ? <><Loader size={11} style={{ marginRight: 4, animation: 'spin 1s linear infinite' }} />Discovering…</>
                        : <><Search size={11} style={{ marginRight: 4 }} />Discover Interfaces</>
                      }
                    </button>
                  </div>

                  {nasDiscoveredIfaces.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                        Check the interfaces to use as WAN bandwidth sources. Leave all unchecked for auto-detection.
                      </div>
                      {nasDiscoveredIfaces.map(iface => (
                        <label key={iface.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer', padding: '4px 0' }}>
                          <input
                            type="checkbox"
                            checked={(nasForm.wanInterfaces || []).includes(iface.name)}
                            onChange={() => toggleWanIface(iface.name)}
                          />
                          <span className="font-mono" style={{ fontSize: '.82rem' }}>{iface.name}</span>
                          {iface.type && <span style={{ fontSize: '.73rem', color: 'var(--text-muted)', marginLeft: 4 }}>{iface.type}</span>}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>
                      {(nasForm.wanInterfaces || []).length > 0 ? (
                        <div>
                          <div style={{ marginBottom: 4 }}>Currently configured:</div>
                          {nasForm.wanInterfaces.map(n => (
                            <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 8, padding: '2px 8px', background: 'var(--cb-dbeafe)', color: 'var(--primary)', borderRadius: 4, fontSize: '.78rem' }}>
                              {n}
                              <button onClick={() => toggleWanIface(n)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', padding: 0, marginLeft: 2 }}>×</button>
                            </span>
                          ))}
                          <div style={{ marginTop: 6 }}>Click "Discover Interfaces" to see all available interfaces.</div>
                        </div>
                      ) : (
                        'Click "Discover Interfaces" to fetch WAN interfaces from the Mikrotik.'
                      )}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button className="btn btn-primary btn-sm" onClick={handleNasSave} disabled={nasSaving}>
                  <Save size={12} style={{ marginRight: 5 }} />{nasSaving ? 'Saving…' : 'Save'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => { setShowNasForm(false); setNasForm(emptyNAS); setEditingNasId(null); setNasDiscoveredIfaces([]); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* NAS list */}
          <div className="card-body" style={{ padding: 0 }}>
            {nasList.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                <Server size={32} style={{ marginBottom: 8, opacity: .4 }} />
                <div style={{ fontSize: '.88rem' }}>No NAS devices configured yet.</div>
                <div style={{ fontSize: '.8rem', marginTop: 4 }}>Click "Add NAS" to register a Network Access Server.</div>
              </div>
            ) : (
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>IP : Port</th>
                    <th>Username</th>
                    <th>WAN Interfaces</th>
                    <th>Status</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {nasList.map(nas => (
                    <tr key={nas._id} style={{ opacity: nas.isEnabled === false ? 0.5 : 1 }}>
                      <td style={{ fontWeight: 500 }}>{nas.name}</td>
                      <td className="font-mono" style={{ fontSize: '.83rem' }}>{nas.ip}:{nas.port}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>{nas.username || '—'}</td>
                      <td style={{ fontSize: '.78rem', color: 'var(--text-muted)', maxWidth: 180 }}>
                        {(nas.wanInterfaces || []).length === 0
                          ? <span style={{ color: 'var(--c-94a3b8)', fontStyle: 'italic' }}>Auto</span>
                          : (nas.wanInterfaces || []).map(n => (
                              <span key={n} style={{ display: 'inline-block', background: 'var(--cb-dbeafe)', color: 'var(--primary)', borderRadius: 3, padding: '1px 6px', marginRight: 4, marginBottom: 2, fontSize: '.73rem' }}>
                                {n.length > 20 ? n.slice(0, 20) + '…' : n}
                              </span>
                            ))
                        }
                      </td>
                      <td>
                        {nas.lastTestResult === 'success' && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.78rem', color: 'var(--success)', fontWeight: 600 }}>
                            <CheckCircle size={13} /> OK
                          </span>
                        )}
                        {nas.lastTestResult === 'failed' && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.78rem', color: 'var(--danger)', fontWeight: 600 }}>
                            <XCircle size={13} /> Failed
                          </span>
                        )}
                        {!nas.lastTestResult && <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" style={{ padding: '3px 8px', fontSize: '.75rem' }} onClick={() => handleNasTest(nas._id)} disabled={nasTesting === nas._id}>
                            {nasTesting === nas._id ? <Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={11} />}
                          </button>
                          <button className="btn btn-secondary btn-sm" style={{ padding: '3px 8px', fontSize: '.75rem' }} onClick={() => handleNasEdit(nas)}>
                            <Pencil size={11} />
                          </button>
                          <button className="btn btn-danger btn-sm" style={{ padding: '3px 8px', fontSize: '.75rem' }} onClick={() => handleNasDelete(nas._id)} disabled={nasDeleting === nas._id}>
                            {nasDeleting === nas._id ? <Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={11} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── WhatsApp API Tab ── */}

      {/* ── Payments Tab ── */}
      {tab === 'payments' && (
        <div>
          {/* CableTV / Broadband first, then its two payment options (2026-10-22). Only one
              option per service is active for customers — the switch below saves just that
              (catvUpi/broadbandUpi.activeMethod). CableTV's gateway is the Entity Gateways list;
              the old CableTV Online PG card and Gateway Routing are hidden, not deleted (the
              routing still decides for entities with no gateway of their own). */}
          {(() => {
            const svc = paymentsSubTab.startsWith('broadband') ? 'broadband' : 'catv';
            const isQr = paymentsSubTab.endsWith('-upi');
            const upi = svc === 'catv' ? catvUpi : broadbandUpi;
            const setUpi = svc === 'catv' ? setCatvUpi : setBroadbandUpi;
            const active = upi.activeMethod || 'gateway';
            const names = svc === 'catv' ? ['Entity Gateway', 'Entity QR'] : ['Gateway', 'QR'];
            const btn = (on, onClick, Icon, label, size = 'btn-sm') => (
              <button key={label} className={`btn ${size} ${on ? 'btn-primary' : 'btn-secondary'}`} onClick={onClick}>
                <Icon size={13} style={{ marginRight: 5 }} />{label}
              </button>
            );
            const activate = async (method) => {
              try {
                await (svc === 'catv' ? settingsAPI.saveCatvUpi : settingsAPI.saveBroadbandUpi)({ activeMethod: method });
                setUpi(f => ({ ...f, activeMethod: method }));
                toast.success(`${svc === 'catv' ? 'CableTV' : 'Broadband'} customers now pay by ${method === 'qr' ? names[1] : names[0]}`);
              } catch (err) { toast.error(err.response?.data?.message || 'Could not switch'); }
            };
            return (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  {btn(svc === 'catv', () => setPaymentsSubTab('catv-pg'), Tv, 'CableTV', '')}
                  {btn(svc === 'broadband', () => setPaymentsSubTab('broadband-pg'), Cable, 'Broadband', '')}
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  {btn(!isQr, () => setPaymentsSubTab(`${svc}-pg`), CreditCard, `${names[0]}${active === 'gateway' ? ' · Active' : ''}`)}
                  {btn(isQr, () => setPaymentsSubTab(`${svc}-upi`), QrCode, `${names[1]}${active === 'qr' ? ' · Active' : ''}`)}
                </div>
                <div className="card" style={{ marginBottom: 16 }}>
                  <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '.88rem' }}>
                      {names[isQr ? 1 : 0]} is <strong>{(active === 'qr') === isQr ? 'active' : 'off'}</strong> for {svc === 'catv' ? 'CableTV' : 'Broadband'} customers.
                    </span>
                    {(active === 'qr') !== isQr && (
                      <button className="btn btn-primary btn-sm" onClick={() => activate(isQr ? 'qr' : 'gateway')}>
                        Use {names[isQr ? 1 : 0]} instead of {names[isQr ? 0 : 1]}
                      </button>
                    )}
                  </div>
                </div>
              </>
            );
          })()}

          {/* Named profiles + amount/service routing rules (2026-07-28) — supersedes the two
              per-service Online PG tabs above, which stay put until the legacy configs are
              retired. See backend services/gatewayRouter.js. */}
          {paymentsSubTab === 'routing' && <GatewayRoutingTab />}
          {paymentsSubTab === 'catv-pg' && <EntityGatewaysTab />}

          {/* CableTV and Broadband each have a fully independent gateway config
              (2026-07-27) — this same card renders under both -pg tabs, just scoped to
              whichever service's slice of state (paymentGateways[pgSvc]/pgUi[pgSvc]). */}
          {paymentsSubTab === 'broadband-pg' && (() => {
            const pgSvc = paymentsSubTab === 'catv-pg' ? 'catv' : 'broadband';
            const pg = paymentGateways[pgSvc];
            const ui = pgUi[pgSvc];
            const svcLabel = pgSvc === 'catv' ? 'CableTV' : 'Broadband';
            const routing = serviceRouting[pgSvc];
            // The dropdown collapses mode+profile into one value so it reads as a single
            // question ("who takes this service's money?") rather than two coupled fields.
            const routingValue = routing.mode === 'profile' ? `profile:${routing.profile || ''}` : routing.mode;
            const pinned = gatewayProfiles.find(p => p._id === routing.profile);
            return (
      <>
      <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body">
            <SECTION title={`Which gateway takes ${svcLabel} payments`}>
              <FIELD label="Pay through">
                <select
                  className="form-control"
                  style={{ maxWidth: 380 }}
                  value={routingValue}
                  onChange={e => {
                    const v = e.target.value;
                    if (v.startsWith('profile:')) setRouting(pgSvc, { mode: 'profile', profile: v.slice(8) });
                    else setRouting(pgSvc, { mode: v, profile: null });
                  }}
                >
                  <option value="rules">Gateway Routing rules (amount-based)</option>
                  <option value="split">Two separate APIs, split by amount</option>
                  {gatewayProfiles.map(p => (
                    <option key={p._id} value={`profile:${p._id}`} disabled={!p.isEnabled}>
                      Profile — {p.name}{p.isEnabled ? '' : ' (disabled)'}
                    </option>
                  ))}
                  <option value="custom">Custom — this service's own credentials below</option>
                </select>
              </FIELD>
              <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                {routing.mode === 'rules'
                  ? <>Every {svcLabel} payment is matched against the rule list on the <strong>Gateway Routing</strong> tab, so the amount decides which merchant account takes it.</>
                  : routing.mode === 'profile'
                    ? <>Every {svcLabel} payment goes through <strong>{pinned?.name || 'the selected profile'}</strong>, whatever the amount — the routing rules are skipped for {svcLabel}.</>
                    : routing.mode === 'split'
                      ? <>{svcLabel} payments up to the split amount use one API, anything above it uses the other. Enter both sets of API details below. The routing rules are skipped for {svcLabel}.</>
                      : <>Every {svcLabel} payment uses the credentials entered below. Profiles and routing rules are both skipped for {svcLabel}.</>}
              </p>
              {gatewayProfiles.length === 0 && (
                <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', margin: '8px 0 0' }}>
                  No gateway profiles exist yet — create one on the <strong>Gateway Routing</strong> tab to pick it here.
                </p>
              )}
            </SECTION>
            {/* Split mode saves the credentials and the choice together further down — a
                "Save Gateway Choice" here would just 400 on the profiles it hasn't got yet. */}
            {routing.mode !== 'split' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-primary" onClick={() => handleSaveGatewayChoice(pgSvc)} disabled={routingSaving === pgSvc}>
                  <Save size={14} style={{ marginRight: 6 }} />
                  {routingSaving === pgSvc ? 'Saving…' : 'Save Gateway Choice'}
                </button>
              </div>
            )}
          </div>
      </div>

      {routing.mode === 'split' && (
        <AmountSplitGateways
          service={pgSvc}
          serviceLabel={svcLabel}
          profiles={gatewayProfiles}
          routing={routing}
          onSaved={reloadGatewayProfiles}
        />
      )}

      {/* The legacy per-service credential form. Only relevant when this service is pinned
          to 'custom' — under the other two modes the credentials that matter live on a
          profile instead, and showing this form would just be a second thing to keep in sync. */}
      {routing.mode === 'custom' && (
      <div className="card">
          <div className="card-body">
            <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', marginTop: 0, marginBottom: 20 }}>
              {svcLabel} customers (dtd/day-to-day accounts) can pay online via this gateway
              on the customer portal Renew page. This is independent of the other service's Online PG config.
            </p>
            <SECTION title="Online PG — Provider">
              <FIELD label="Provider">
                <select
                  className="form-control"
                  style={{ maxWidth: 280 }}
                  value={pg.provider}
                  onChange={e => setPg(pgSvc, 'provider', e.target.value)}
                >
                  <option value="razorpay">Razorpay</option>
                  <option value="ablepay">AblePay</option>
                  <option value="cashfree">Cashfree</option>
                  <option value="payu">PayU</option>
                  <option value="other">Other</option>
                </select>
              </FIELD>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem', marginTop: 4 }}>
                <input type="checkbox" checked={pg.testMode}
                  onChange={e => setPg(pgSvc, 'testMode', e.target.checked)} />
                Test / Sandbox mode
              </label>
            </SECTION>

            {pg.provider === 'ablepay' ? (
              <>
                <SECTION title="Online PG — AblePay Credentials">
                  <FIELD label="API Key" required hint="From AblePay's merchant dashboard (pgmrm.ablepay.co.in)">
                    <input className="form-control font-mono" value={pg.ablepayApiKey}
                      onChange={e => setPg(pgSvc, 'ablepayApiKey', e.target.value.trim())} placeholder="e.g. 9b62ff8e-f03b-1587-afds-b630edad99df" style={{ maxWidth: 400 }} />
                  </FIELD>
                  <FIELD label="Salt" required hint={pg.ablepaySalt === MASKED ? 'Leave unchanged to keep current secret' : 'Used to sign every request/response — never shared publicly'}>
                    <div style={{ position: 'relative', maxWidth: 400 }}>
                      <input
                        className="form-control font-mono"
                        type={ui.showAblepaySalt ? 'text' : 'password'}
                        value={pg.ablepaySalt}
                        onFocus={() => { if (pg.ablepaySalt === MASKED) setPg(pgSvc, 'ablepaySalt', ''); }}
                        onBlur={() => { if (pg.ablepaySalt === '') setPg(pgSvc, 'ablepaySalt', MASKED); }}
                        onChange={e => setPg(pgSvc, 'ablepaySalt', e.target.value)}
                        style={{ paddingRight: 40 }}
                      />
                      <button type="button" onClick={() => setPgUiField(pgSvc, 'showAblepaySalt', !ui.showAblepaySalt)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                        {ui.showAblepaySalt ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </FIELD>
                  <FIELD label="Gateway URL" required hint="The {pg_api_url} domain from AblePay's onboarding — just the domain, no https:// or path">
                    <input className="form-control font-mono" value={pg.ablepayPgApiUrl}
                      onChange={e => setPg(pgSvc, 'ablepayPgApiUrl', e.target.value.trim())} placeholder="e.g. pgapi.ablepay.co.in" style={{ maxWidth: 400 }} />
                  </FIELD>
                </SECTION>

                <SECTION title="Online PG — AblePay Display">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem' }}>
                    <input type="checkbox" checked={pg.ablepayShowConvenienceFee}
                      onChange={e => setPg(pgSvc, 'ablepayShowConvenienceFee', e.target.checked)} />
                    Show the convenience fee breakdown to the customer on AblePay's payment page
                  </label>
                </SECTION>
              </>
            ) : (
              <SECTION title="Online PG — API Credentials">
                <FIELD label="Key ID" required hint="Public API key / merchant ID from the gateway dashboard">
                  <input className="form-control font-mono" value={pg.keyId}
                    onChange={e => setPg(pgSvc, 'keyId', e.target.value.trim())} placeholder="e.g. rzp_test_xxxxxxxxxxxx" style={{ maxWidth: 400 }} />
                </FIELD>
                <FIELD label="Key Secret" required hint={pg.keySecret === MASKED ? 'Leave unchanged to keep current secret' : ''}>
                  <div style={{ position: 'relative', maxWidth: 400 }}>
                    <input
                      className="form-control font-mono"
                      type={ui.showSecret ? 'text' : 'password'}
                      value={pg.keySecret}
                      onFocus={() => { if (pg.keySecret === MASKED) setPg(pgSvc, 'keySecret', ''); }}
                      onBlur={() => { if (pg.keySecret === '') setPg(pgSvc, 'keySecret', MASKED); }}
                      onChange={e => setPg(pgSvc, 'keySecret', e.target.value)}
                      style={{ paddingRight: 40 }}
                    />
                    <button type="button" onClick={() => setPgUiField(pgSvc, 'showSecret', !ui.showSecret)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                      {ui.showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </FIELD>
                <FIELD label="Webhook Secret" hint={pg.webhookSecret === MASKED ? 'Leave unchanged to keep current secret' : 'Used to verify payment webhook signatures'}>
                  <div style={{ position: 'relative', maxWidth: 400 }}>
                    <input
                      className="form-control font-mono"
                      type={ui.showWebhookSecret ? 'text' : 'password'}
                      value={pg.webhookSecret}
                      onFocus={() => { if (pg.webhookSecret === MASKED) setPg(pgSvc, 'webhookSecret', ''); }}
                      onBlur={() => { if (pg.webhookSecret === '') setPg(pgSvc, 'webhookSecret', MASKED); }}
                      onChange={e => setPg(pgSvc, 'webhookSecret', e.target.value)}
                      style={{ paddingRight: 40 }}
                    />
                    <button type="button" onClick={() => setPgUiField(pgSvc, 'showWebhookSecret', !ui.showWebhookSecret)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                      {ui.showWebhookSecret ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </FIELD>
              </SECTION>
            )}

            <SECTION title="Online PG — Customer Surcharge">
              <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: -4, marginBottom: 12 }}>
                When checked, the customer pays the plan price plus a gateway fee — you always net the full
                plan amount either way. Leave unchecked and you absorb the gateway's cut yourself.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.9rem', marginBottom: 10, fontWeight: 600 }}>
                <input type="checkbox" checked={pg.passFeeToCustomer}
                  onChange={e => setPg(pgSvc, 'passFeeToCustomer', e.target.checked)} />
                Charge the gateway fee to the customer (I don't bear any loss)
              </label>
              {pg.passFeeToCustomer && (
                <>
                  <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 10 }}>
                    Enter each payment method's actual rate — since the customer only picks their method on the
                    gateway's own page (after we've already sent the request), the <strong>highest</strong> rate
                    entered below is the one actually applied, so you're covered no matter which they choose.
                  </p>
                  {[
                    { key: 'upi', label: 'UPI' },
                    { key: 'debitCard', label: 'Debit Card' },
                    { key: 'creditCard', label: 'Credit Card' },
                    { key: 'netbanking', label: 'Net Banking' },
                  ].map(({ key, label }) => (
                    <div key={key} style={{ display: 'flex', gap: 14, alignItems: 'flex-end', marginBottom: 10 }}>
                      <FIELD label={`${label} — Fee %`}>
                        <input type="number" step="0.01" min="0" max="100" className="form-control" style={{ maxWidth: 130 }}
                          value={pg.feesByMethod[key].percent}
                          onChange={e => setPgFeeMethod(pgSvc, key, 'percent', Number(e.target.value))} />
                      </FIELD>
                      <FIELD label={`${label} — Flat Fee (₹)`}>
                        <input type="number" step="0.01" min="0" className="form-control" style={{ maxWidth: 130 }}
                          value={pg.feesByMethod[key].flat}
                          onChange={e => setPgFeeMethod(pgSvc, key, 'flat', Number(e.target.value))} />
                      </FIELD>
                    </div>
                  ))}
                </>
              )}
            </SECTION>

            <SECTION title="Online PG — Status">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem' }}>
                <input type="checkbox" checked={pg.isEnabled}
                  onChange={e => setPg(pgSvc, 'isEnabled', e.target.checked)} />
                Enable online payment collection via this gateway
              </label>
            </SECTION>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleTestPaymentGateway(pgSvc)}
                disabled={ui.testing || (pg.provider === 'ablepay' ? !pg.ablepayApiKey : pg.provider !== 'razorpay' || !pg.keyId)}
              >
                {ui.testing
                  ? <><Loader size={12} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} />Testing…</>
                  : ui.testResult === 'success'
                    ? <><CheckCircle size={12} style={{ marginRight: 5, color: 'var(--success)' }} />Connected</>
                    : ui.testResult === 'failed'
                      ? <><XCircle size={12} style={{ marginRight: 5, color: 'var(--danger)' }} />Failed</>
                      : <><RefreshCw size={12} style={{ marginRight: 5 }} />Test Connection</>
                }
              </button>
              <button className="btn btn-primary" onClick={() => handleSavePaymentGateway(pgSvc)} disabled={ui.saving}>
                <Save size={14} style={{ marginRight: 6 }} />
                {ui.saving ? 'Saving…' : `Save ${svcLabel} Online PG Settings`}
              </button>
            </div>
          </div>
      </div>
      )}
      </>
            );
          })()}

          {paymentsSubTab === 'catv-upi' && (
      <div className="card">
          <div className="card-body">
            <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', marginTop: 0, marginBottom: 20 }}>
              One or more UPI QRs per entity. A customer sees their entity's QRs; an entity with none set
              uses the "All others" QRs.
            </p>
            <SECTION title="UPI QR — Payee Name">
              <FIELD label="UPI Payee Name" hint="Shown under the QR code and passed to the UPI app as the payee name">
                <input className="form-control" style={{ maxWidth: 320 }} value={catvUpi.payeeName}
                  onChange={e => setCatvUpi(f => ({ ...f, payeeName: e.target.value }))}
                  placeholder="e.g. Laxmi Cable Vision" />
              </FIELD>
            </SECTION>

            <SECTION title="UPI QR Entries">
              <div style={{ display: 'grid', gap: 10 }}>
                {catvUpi.entries.map((entry, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                    <FIELD label="Label" hint={i === 0 ? 'e.g. bank/app name shown to the customer' : undefined}>
                      <input className="form-control" style={{ width: 200 }} value={entry.label}
                        onChange={e => setCatvUpiEntry(i, 'label', e.target.value)}
                        placeholder="e.g. HDFC Bank" />
                    </FIELD>
                    <FIELD label="UPI ID">
                      <input className="form-control font-mono" style={{ width: 260 }} value={entry.upiId}
                        onChange={e => setCatvUpiEntry(i, 'upiId', e.target.value)}
                        placeholder="e.g. business@hdfcbank" />
                    </FIELD>
                    <FIELD label="Entity" hint={i === 0 ? 'Customers of this entity see this QR; blank = everyone else' : undefined}>
                      <select className="form-control" style={{ width: 150 }} value={entry.entity || ''}
                        onChange={e => setCatvUpiEntry(i, 'entity', e.target.value)}>
                        <option value="">All others</option>
                        {['SPBP0042', 'SPBP0368', 'SPBP0369', 'SPBP0370'].map(code => <option key={code} value={code}>{code}</option>)}
                      </select>
                    </FIELD>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeCatvUpiEntry(i)} style={{ marginBottom: 2 }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addCatvUpiEntry} style={{ marginTop: catvUpi.entries.length ? 12 : 0 }}>
                <Plus size={13} style={{ marginRight: 5 }} />Add UPI Entry
              </button>
            </SECTION>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-primary" onClick={handleSaveCatvUpi} disabled={catvUpiSaving}>
                <Save size={14} style={{ marginRight: 6 }} />
                {catvUpiSaving ? 'Saving…' : 'Save CableTV UPI Settings'}
              </button>
            </div>
          </div>
      </div>
          )}

          {paymentsSubTab === 'broadband-upi' && (
      <div className="card">
          <div className="card-body">
            <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', marginTop: 0, marginBottom: 20 }}>
              One or more UPI QRs per entity. A customer sees their entity's QRs; an entity with none set
              uses the "All others" QRs.
            </p>
            <SECTION title="Payee Name">
              <FIELD label="UPI Payee Name" hint="Shown under the QR code and passed to the UPI app as the payee name">
                <input className="form-control" style={{ maxWidth: 320 }} value={broadbandUpi.payeeName}
                  onChange={e => setBroadbandUpi(f => ({ ...f, payeeName: e.target.value }))}
                  placeholder="e.g. AngelNet Broadband" />
              </FIELD>
            </SECTION>

            <SECTION title="UPI QR Entries">
              <div style={{ display: 'grid', gap: 10 }}>
                {broadbandUpi.entries.map((entry, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                    <FIELD label="Label" hint={i === 0 ? 'e.g. bank/app name shown to the customer' : undefined}>
                      <input className="form-control" style={{ width: 200 }} value={entry.label}
                        onChange={e => setUpiEntry(i, 'label', e.target.value)}
                        placeholder="e.g. HDFC Bank" />
                    </FIELD>
                    <FIELD label="UPI ID">
                      <input className="form-control font-mono" style={{ width: 260 }} value={entry.upiId}
                        onChange={e => setUpiEntry(i, 'upiId', e.target.value)}
                        placeholder="e.g. business@hdfcbank" />
                    </FIELD>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeUpiEntry(i)} style={{ marginBottom: 2 }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addUpiEntry} style={{ marginTop: broadbandUpi.entries.length ? 12 : 0 }}>
                <Plus size={13} style={{ marginRight: 5 }} />Add UPI Entry
              </button>
            </SECTION>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-primary" onClick={handleSaveBroadbandUpi} disabled={bbUpiSaving}>
                <Save size={14} style={{ marginRight: 6 }} />
                {bbUpiSaving ? 'Saving…' : 'Save Broadband UPI Settings'}
              </button>
            </div>
          </div>
      </div>
          )}
        </div>
      )}

      {tab === 'imbs' && (
        <div className="card">
          <div className="card-body">
            <SECTION title="Credentials">
              <FIELD label="Site ID" required hint="site_id shown in your Netzur admin panel">
                <input className="form-control font-mono" value={imbs.siteId}
                  onChange={e => setImbs('siteId', e.target.value.trim())} placeholder="e.g. 1444" style={{ maxWidth: 280 }} />
              </FIELD>
              <FIELD label="Token" required hint={imbs.token === MASKED ? 'Leave unchanged to keep current token' : 'API bearer token from the Netzur admin panel'}>
                <div style={{ position: 'relative', maxWidth: 400 }}>
                  <input
                    className="form-control font-mono"
                    type={imbsShowToken ? 'text' : 'password'}
                    value={imbs.token}
                    onFocus={() => { if (imbs.token === MASKED) setImbs('token', ''); }}
                    onBlur={() => { if (imbs.token === '') setImbs('token', MASKED); }}
                    onChange={e => setImbs('token', e.target.value)}
                    style={{ paddingRight: 40 }}
                  />
                  <button type="button" onClick={() => setImbsShowToken(s => !s)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                    {imbsShowToken ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </FIELD>
            </SECTION>

            <SECTION title="Panel login (e-KYC deep link)">
              <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
                Optional. Lets the CRM open a customer's Netzur Documents page (where "Verify e-KYC" is) directly.
                This is a <b>web-panel</b> login, not the API token. Please create a separate Netzur user with limited rights for it,
                and don't turn on OTP for that user (the CRM can't type an OTP).
              </div>
              <FIELD label="Panel username">
                <input className="form-control" value={imbs.panelUsername || ''} autoComplete="off"
                  onChange={e => setImbs('panelUsername', e.target.value.trim())} style={{ maxWidth: 280 }} />
              </FIELD>
              <FIELD label="Panel password" hint={imbs.panelPassword === MASKED ? 'Leave unchanged to keep current password' : ''}>
                <div style={{ position: 'relative', maxWidth: 400 }}>
                  <input
                    className="form-control font-mono"
                    type={imbsShowPanelPw ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={imbs.panelPassword || ''}
                    onFocus={() => { if (imbs.panelPassword === MASKED) setImbs('panelPassword', ''); }}
                    onBlur={() => { if (imbs.panelPassword === '' && imbs.panelUsername) setImbs('panelPassword', MASKED); }}
                    onChange={e => setImbs('panelPassword', e.target.value)}
                    style={{ paddingRight: 40 }}
                  />
                  <button type="button" onClick={() => setImbsShowPanelPw(s => !s)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                    {imbsShowPanelPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </FIELD>
              <button className="btn btn-secondary btn-sm" onClick={handleTestImbsPanel}
                disabled={imbsPanelTesting || !imbs.panelUsername || imbs.panelPassword !== MASKED}
                title="Save first, then test — the test uses the saved login.">
                {imbsPanelTesting
                  ? <><Loader size={12} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} />Testing…</>
                  : <><RefreshCw size={12} style={{ marginRight: 5 }} />Test panel login (saved)</>}
              </button>
            </SECTION>

            <SECTION title="Status">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem' }}>
                <input type="checkbox" checked={imbs.isEnabled}
                  onChange={e => setImbs('isEnabled', e.target.checked)} />
                Enable Netzur integration (subscriber sync, payments, recharge, sessions)
              </label>
            </SECTION>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleTestImbs}
                disabled={imbsTesting || !imbs.siteId || !imbs.token}
              >
                {imbsTesting
                  ? <><Loader size={12} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} />Testing…</>
                  : imbsTestResult === 'success'
                    ? <><CheckCircle size={12} style={{ marginRight: 5, color: 'var(--success)' }} />Connected</>
                    : imbsTestResult === 'failed'
                      ? <><XCircle size={12} style={{ marginRight: 5, color: 'var(--danger)' }} />Failed</>
                      : <><RefreshCw size={12} style={{ marginRight: 5 }} />Test Connection</>
                }
              </button>
              <button className="btn btn-primary" onClick={handleSaveImbs} disabled={imbsSaving}>
                <Save size={14} style={{ marginRight: 6 }} />
                {imbsSaving ? 'Saving…' : 'Save Netzur Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'playboxtv' && (
        <div className="card">
          <div className="card-body">
            <SECTION title="Credentials">
              <FIELD label="API Key" required hint={playboxtv.apiKey === MASKED ? 'Leave unchanged to keep current key' : 'x-api-key given by PlayBoxTV (shared via email)'}>
                <div style={{ position: 'relative', maxWidth: 400 }}>
                  <input
                    className="form-control font-mono"
                    type={pbShowApiKey ? 'text' : 'password'}
                    value={playboxtv.apiKey}
                    onFocus={() => { if (playboxtv.apiKey === MASKED) setPlayboxtv('apiKey', ''); }}
                    onBlur={() => { if (playboxtv.apiKey === '') setPlayboxtv('apiKey', MASKED); }}
                    onChange={e => setPlayboxtv('apiKey', e.target.value)}
                    style={{ paddingRight: 40 }}
                  />
                  <button type="button" onClick={() => setPbShowApiKey(s => !s)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                    {pbShowApiKey ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </FIELD>
              <FIELD label="Partner Key" required hint={playboxtv.partnerKey === MASKED ? 'Leave unchanged to keep current key' : 'Generated for your operator account in the PlayBoxTV CRM'}>
                <div style={{ position: 'relative', maxWidth: 400 }}>
                  <input
                    className="form-control font-mono"
                    type={pbShowPartnerKey ? 'text' : 'password'}
                    value={playboxtv.partnerKey}
                    onFocus={() => { if (playboxtv.partnerKey === MASKED) setPlayboxtv('partnerKey', ''); }}
                    onBlur={() => { if (playboxtv.partnerKey === '') setPlayboxtv('partnerKey', MASKED); }}
                    onChange={e => setPlayboxtv('partnerKey', e.target.value)}
                    style={{ paddingRight: 40 }}
                  />
                  <button type="button" onClick={() => setPbShowPartnerKey(s => !s)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                    {pbShowPartnerKey ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </FIELD>
              <FIELD label="Base URL" hint="Only change this if PlayBoxTV gives you a different API host">
                <input className="form-control font-mono" value={playboxtv.baseUrl}
                  onChange={e => setPlayboxtv('baseUrl', e.target.value.trim())} style={{ maxWidth: 400 }} />
              </FIELD>
              <FIELD label="Token Issuer (iss)" hint="Value PlayBoxTV assigns for minting the Bearer token — confirm with PlayBoxTV support if unsure">
                <input className="form-control font-mono" value={playboxtv.tokenIss}
                  onChange={e => setPlayboxtv('tokenIss', e.target.value)} style={{ maxWidth: 280 }} />
              </FIELD>
              <FIELD label="Token Audience (aud)" hint="Value PlayBoxTV assigns for minting the Bearer token — confirm with PlayBoxTV support if unsure">
                <input className="form-control font-mono" value={playboxtv.tokenAud}
                  onChange={e => setPlayboxtv('tokenAud', e.target.value)} style={{ maxWidth: 280 }} />
              </FIELD>
            </SECTION>

            <SECTION title="Status">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem' }}>
                <input type="checkbox" checked={playboxtv.isEnabled}
                  onChange={e => setPlayboxtv('isEnabled', e.target.checked)} />
                Enable PlayBoxTV integration (OTT addon pack assignment for broadband customers)
              </label>
              <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 10 }}>
                PlayBoxTV only accepts API calls from a whitelisted static IP — share this
                server's outbound IP with PlayBoxTV support before testing, or every call
                below will fail with an authorization error.
              </div>
            </SECTION>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleTestPlayboxtv}
                disabled={pbTesting || !playboxtv.apiKey || !playboxtv.partnerKey}
              >
                {pbTesting
                  ? <><Loader size={12} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} />Testing…</>
                  : pbTestResult === 'success'
                    ? <><CheckCircle size={12} style={{ marginRight: 5, color: 'var(--success)' }} />Connected</>
                    : pbTestResult === 'failed'
                      ? <><XCircle size={12} style={{ marginRight: 5, color: 'var(--danger)' }} />Failed</>
                      : <><RefreshCw size={12} style={{ marginRight: 5 }} />Test Connection</>
                }
              </button>
              <button className="btn btn-primary" onClick={handleSavePlayboxtv} disabled={pbSaving}>
                <Save size={14} style={{ marginRight: 6 }} />
                {pbSaving ? 'Saving…' : 'Save PlayBoxTV Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'googleSheets' && <GoogleSheetsSettingsTab />}
      {tab === 'sms' && <SmsTab />}
      {tab === 'ads' && <AdsPage />}
      {tab === 'import' && <ImportPage />}
      {tab === 'history-import' && <HistoryImportPage service="catv" />}
      {tab === 'history-import-broadband' && <HistoryImportPage service="broadband" />}
      {tab === 'app-view' && <AppViewSettingsTab />}
      {tab === 'dashboard-widgets' && <DashboardWidgetSettingsTab />}

      {/* ── Bottom save bar (OLT tabs only) ── */}
      {tab !== 'billing' && tab !== 'nas' && tab !== 'payments'
        && tab !== 'sms' && tab !== 'ads' && tab !== 'import' && tab !== 'history-import' && tab !== 'history-import-broadband' && tab !== 'app-view' && tab !== 'dashboard-widgets' && tab !== 'gtplSaathi' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button className="btn btn-secondary btn-sm" onClick={handleTest} disabled={testing || !form.ip}>
            {testing ? 'Testing…' : 'Test Connection'}
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={14} style={{ marginRight: 6 }} />
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
